<template>
  <v-row no-gutters align="center" justify="start">
    <v-col cols="6" sm="4" md="4" lg="4" class="py-1">
      <span class="ml-1 font-weight-medium">RUT del pagador</span>
      <v-text-field v-model="$v.fields.docNumberMasked.$model" v-dni="'CL'" :error="$v.fields.docNumberMasked.$error || $v.fields.docNumber.$error" background-color="white" outlined required single-line dense maxlength="12" persistent-hint hide-details />
    </v-col>
    <v-col cols="12" class="py-1">
      <span class="ml-1 font-weight-medium">Titular de la tarjeta</span>
      <v-text-field v-model="$v.fields.cardholderName.$model" :error="$v.fields.cardholderName.$error" background-color="white" outlined required single-line dense maxlength="80" persistent-hint hide-details />
    </v-col>
    <v-col cols="12" sm="6" md="7" lg="7" class="py-1 pr-sm-3">
      <span class="ml-1 font-weight-medium">Número de la tarjeta</span>
      <div class="mp-field" :class="fieldClass('cardNumber')">
        <div id="mpCardNumber" class="mp-field__input"></div>
        <v-fade-transition leave-absolute>
          <img v-if="brand" class="mp-field__brand" :src="require(`@/assets/appAsset-cardbrand--${brand}.svg`)" :alt="brand" height="20" />
        </v-fade-transition>
      </div>
    </v-col>
    <v-col cols="6" sm="3" md="3" lg="3" class="py-1 pr-3">
      <span class="ml-1 font-weight-medium">Vencimiento</span>
      <div class="mp-field" :class="fieldClass('expirationDate')">
        <div id="mpExpirationDate" class="mp-field__input"></div>
      </div>
    </v-col>
    <v-col cols="6" sm="3" md="2" lg="2" class="py-1">
      <span class="ml-1 font-weight-medium">CVV</span>
      <div class="mp-field" :class="fieldClass('securityCode')">
        <div id="mpSecurityCode" class="mp-field__input"></div>
      </div>
    </v-col>
  </v-row>
</template>
<script>
import { maxLength, required } from 'vuelidate/lib/validators'
import { rutValidator } from 'vue-dni'
import GenericViewMixin from '@/mixins/GenericViewMixin'
import MercadopagoSdkMixin from '@/mixins/MercadopagoSdkMixin'
import { resolveCardTokenError, toMercadopagoRut } from '@/helpers/mercadopago'

// Mercado Pago payment_method_id -> the card brand asset we ship.
const BRANDS = {
  amex: 'AmericanExpress',
  debmaster: 'MasterCard',
  debvisa: 'Visa',
  diners: 'Diners',
  master: 'MasterCard',
  redcompra: 'RedCompra',
  visa: 'Visa'
}

const FIELDS = ['cardNumber', 'expirationDate', 'securityCode']

// Applied inside Mercado Pago's iframes, which otherwise render with their own
// typography and stand out next to the Vuetify inputs beside them. The values
// mirror a `v-text-field outlined dense`: 16px text in the theme font stack
// ($body-font-family), the same primary and hint tones, and no padding of its
// own because .mp-field already provides the 12px gutter.
const FIELD_STYLE = {
  color: 'rgba(0, 0, 0, 0.87)',
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Open Sans', 'Helvetica Neue', 'Ubuntu', sans-serif",
  fontSize: '16px',
  fontWeight: '400',
  height: '100%',
  padding: '0',
  placeholderColor: 'rgba(0, 0, 0, 0.38)'
}

export default {
  name: 'mercadopago-secure-fields',
  mixins: [
    // GenericViewMixin registers the `dni` directive, which keeps its listener
    // state on vnode.context: the component rendering v-dni has to own it.
    GenericViewMixin,
    MercadopagoSdkMixin
  ],
  props: {
    publicKey: {
      type: String,
      required: true
    }
  },
  data: () => ({
    brand: '',
    elements: [],
    fields: {
      cardholderName: '',
      docNumber: '',
      docNumberMasked: ''
    },
    focused: '',
    // Card field errors are only painted once the payer has tried to pay, so
    // the form does not turn red while they are still typing.
    touched: false,
    validity: {
      cardNumber: false,
      expirationDate: false,
      securityCode: false
    }
  }),
  watch: {
    'fields.docNumberMasked' () {
      if (this.fields.docNumberMasked) {
        this.$v.fields.docNumber.$model = this.fields.docNumberMasked.replace(/\./g, '')
      }
    }
  },
  async mounted () {
    try {
      const mp = await this.initMercadopago(this.publicKey)

      // srLabel: the visible labels are plain spans, so without this a screen
      // reader announces the iframes as unlabelled.
      const cardNumber = mp.fields.create('cardNumber', {
        placeholder: '•••• •••• •••• ••••',
        srLabel: 'Número de la tarjeta',
        style: FIELD_STYLE
      })
      const expirationDate = mp.fields.create('expirationDate', {
        placeholder: 'MM/AA',
        srLabel: 'Vencimiento de la tarjeta',
        style: FIELD_STYLE
      })
      const securityCode = mp.fields.create('securityCode', {
        placeholder: 'CVV',
        srLabel: 'Código de seguridad',
        style: FIELD_STYLE
      })

      // Replaces the old watcher that guessed the brand off the raw card length:
      // binChange fires exactly when Mercado Pago sees the BIN change, and it
      // also fires with an empty bin so the logo can be cleared.
      cardNumber.on('binChange', async ({ bin }) => {
        if (!bin) {
          this.brand = ''
          return
        }
        try {
          const { results } = await mp.getPaymentMethods({ bin })
          this.brand = (results && results.length && BRANDS[results[0].id]) || ''
        } catch (error) {
          this.brand = ''
        }
      })

      const mounts = {
        cardNumber: { element: cardNumber, container: 'mpCardNumber' },
        expirationDate: { element: expirationDate, container: 'mpExpirationDate' },
        securityCode: { element: securityCode, container: 'mpSecurityCode' }
      }

      FIELDS.forEach((name) => {
        const { element, container } = mounts[name]
        element.on('validityChange', (event) => {
          this.validity[name] = !(event && event.errorMessages && event.errorMessages.length)
        })
        element.on('focus', () => { this.focused = name })
        element.on('blur', () => { this.focused = '' })
        this.elements.push(element)
        element.mount(container)
      })

      this.$emit('ready')
    } catch (error) {
      this.$emit('error', error.message || 'No pudimos cargar el formulario de pago.')
    }
  },
  beforeDestroy () {
    // Same reason as the Brick: leaving stale iframes behind breaks the next
    // mount on these container ids.
    this.elements.forEach((element) => {
      try {
        element.unmount()
      } catch (error) {
        // already gone
      }
    })
    this.elements = []
  },
  methods: {
    fieldClass (name) {
      return {
        'mp-field--focused': this.focused === name,
        'mp-field--error': this.touched && !this.validity[name]
      }
    },
    /**
     * Validate and tokenize. Returns the token id, or null when the form is
     * incomplete or Mercado Pago rejected the card data.
     */
    async createToken () {
      if (!this.mp) {
        this.$emit('error', 'No pudimos cargar el formulario de pago. Recarga la página.')
        return null
      }

      this.touched = true
      this.$v.fields.$touch()

      if (this.$v.fields.$invalid || FIELDS.some(name => !this.validity[name])) {
        this.$emit('error', 'Revisa los datos de tu tarjeta.')
        return null
      }

      try {
        const token = await this.mp.fields.createCardToken({
          cardholderName: this.fields.cardholderName,
          identificationType: 'RUT',
          identificationNumber: toMercadopagoRut(this.fields.docNumber)
        })
        return (token && token.id) || null
      } catch (error) {
        this.$emit('error', resolveCardTokenError(error))
        return null
      }
    }
  },
  validations: {
    fields: {
      cardholderName: {
        required,
        maxLength: maxLength(80)
      },
      docNumber: {
        required,
        maxLength: maxLength(10),
        taxId: function (val) {
          return rutValidator(val)
        }
      },
      docNumberMasked: {
        required,
        maxLength: maxLength(12)
      }
    }
  }
}
</script>
<style scoped>
/* Mirrors a Vuetify `outlined dense` text field: 40px tall, 6px radius
   ($border-radius-root), rgba(0,0,0,.38) border. The padding compensates the
   2px border on focus and error so the height does not jump. */
.mp-field {
  align-items: center;
  background-color: #FFFFFF;
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 6px;
  display: flex;
  height: 40px;
  padding: 0 12px;
  transition: border-color .2s ease;
}
.mp-field--focused {
  border-color: var(--v-primary-base);
  border-width: 2px;
  padding: 0 11px;
}
.mp-field--error {
  border-color: #FF5252;
  border-width: 2px;
  padding: 0 11px;
}
.mp-field__input {
  flex: 1 1 auto;
  height: 100%;
}
.mp-field__brand {
  flex: 0 0 auto;
  margin-left: 8px;
}
/* The SDK injects the iframe after render, so it never carries the scoped
   data-v attribute. Without the deep selector it has no dimensions and the
   field looks empty. */
.mp-field__input >>> iframe {
  display: block;
  height: 100% !important;
  width: 100% !important;
}
</style>
